import { z } from 'zod';

/**
 * Shared contract for where a tenant's per-subaccount Twilio Voice API
 * key (the SECRET used to sign browser voice tokens) is stored, so the
 * WRITER (config-api, during the Workd-only Enable-Phone flow) and the
 * READERS (crm-api's voice-token signer + the derived feature gate)
 * agree on one path + one value shape and can never drift (PD-14218 /
 * PD-14243 P1).
 *
 * **Why SSM, not `instance_setting`:** the API key secret is a true
 * secret. crm-api is read-only on SSM; only config-api can `PutParameter`,
 * so config-api writes it as a `SecureString` (encrypted at rest via KMS),
 * mirroring how `DatabaseProvisioningService.storeTenantDatabaseCredentials`
 * already stores each tenant's DB password. crm-api reads it back with
 * decryption via `AwsParameterService.getParameter`.
 *
 * **What stays in `instance_setting`:** the non-secret subaccount SID
 * (`softphone.twilioSubAccountSid`) — it's hydrated onto `TenantContext`
 * every request and read synchronously by call-control/recording/coaching/
 * e911, so it can't move to SSM without putting SSM on the auth hot path.
 * Only the secret lives in SSM.
 */

/**
 * Builds the SSM Parameter Store path for a tenant's minted voice API key
 * (stored as a JSON `SecureString`). The `crm-api` namespace + tenant-
 * scoped shape mirror the DB-credential path
 * (`/workd/{env}/crm-api/tenant/{alias}/database/password`) so all
 * tenant-scoped secrets share one convention.
 *
 * @param environment deployment env (`staging` | `production`), the same
 *   value `ConfigService.get('environment')` returns in both apps.
 * @param tenantAlias the instance alias (e.g. `auvis`).
 */
export const buildTwilioVoiceKeySsmPath = (
	environment: string,
	tenantAlias: string,
): string =>
	`/workd/${environment}/crm-api/tenant/${tenantAlias}/twilio/voiceKey`;

/**
 * Shape of the JSON stored at {@link buildTwilioVoiceKeySsmPath}. SECRET —
 * never logged, never returned to a browser, never hydrated onto the
 * request context. Validate reads with {@link SoftphoneSubAccountVoiceKeySchema}
 * so a malformed/empty SSM value fails closed (treated as "no key" → the
 * signer falls back to the master account, the gate reports not-provisioned).
 */
export const SoftphoneSubAccountVoiceKeySchema = z.object({
	apiKeySid: z.string(),
	apiKeySecret: z.string(),
});

export type SoftphoneSubAccountVoiceKeyValueModel = z.infer<
	typeof SoftphoneSubAccountVoiceKeySchema
>;
